/**
 * 登录凭据的本地持久化。
 *
 * 单独抽出来的原因是打破循环依赖：axios 拦截器需要读 token，而 auth store 需要调 axios。
 * 如果拦截器直接去读 store，两个模块就互相 import 了。让双方都只依赖这个没有任何依赖的
 * 小模块，环就断开了。
 *
 * <h2>为什么用 localStorage 而不是 httpOnly cookie</h2>
 * httpOnly cookie 能挡住 XSS 偷 token，安全性更高，但它要求前后端同源或者正确配置
 * SameSite 和 CORS 凭据，部署形态会被绑死。这个产品要卖给多个老板，将来可能出现
 * 前端放 CDN、后端在另一个域名的情况，localStorage + Authorization 头对部署没有任何约束。
 *
 * 作为补偿，XSS 的防线放在别处：Vue 默认对插值做 HTML 转义，项目里不使用 v-html，
 * 且 token 有效期只有 12 小时。
 */

const TOKEN_KEY = 'cleanflow.token'
const USER_KEY = 'cleanflow.user'

/**
 * @returns {string | null} 当前 token，没有则返回 null
 */
export function getToken() {
  return localStorage.getItem(TOKEN_KEY)
}

/**
 * @param {string} token
 */
export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token)
}

/**
 * 读取缓存的用户信息。
 *
 * <p>这份数据只用来在刷新页面时<b>立刻</b>渲染出用户名和对应的界面布局，
 * 避免白屏或闪烁。它<b>不是权限依据</b>——用户完全可以手改 localStorage 把自己的角色
 * 写成 OWNER。真正的权限判断永远在后端，每个请求都会重新查库确认角色。
 *
 * @returns {object | null} 用户信息；不存在或已损坏时返回 null
 */
export function getCachedUser() {
  const raw = localStorage.getItem(USER_KEY)
  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw)
  } catch {
    // 存进去的不是合法 JSON，说明被手改过或被别的代码覆盖了。
    // 直接丢掉并当作未登录处理，总比让一个坏掉的对象在应用里到处流窜好。
    localStorage.removeItem(USER_KEY)
    return null
  }
}

/**
 * @param {object} user
 */
export function setCachedUser(user) {
  localStorage.setItem(USER_KEY, JSON.stringify(user))
}

/**
 * 清除所有登录痕迹。退出登录、token 失效、账号被停用时都要调。
 */
export function clearAuthStorage() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
}
